import { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useTeacherTraining } from "../../../libs/data-access";
import { calculateDefaultSession, showToast } from "../../../utils/utility";
import useMasterData from "../../Hooks/useMasterData";
import { AppContext } from "../../../AppContext";
import { ROLES } from "../../../Routes/routes";
import { generateExcelReport } from "../../TeacherTrainingAttendance/utils/generateTeacherAttendanceReport.util";

/**
 * TODO: confirm endpoint names with backend. Expected response shapes:
 *  SUMMARY    -> [{ teachersAssigned, preCompleted, preCompletedPct, postCompleted,
 *                   postCompletedPct, avgPreScore, avgPostScore, learningGain,
 *                   excellentPct, goodPct, averagePct, needsSupportPct }]
 *  PERFORMANCE-> [{ name, avgPostScore }]   (districts when districtId=0, else blocks)
 *  TREND      -> [{ label, count }]         (cumulative submissions over time)
 */
const EP = {
  SUMMARY: "/GetTrainingAnalyticsSummary",
  PERFORMANCE: "/GetTrainingAnalyticsPerformance",
  TREND: "/GetTrainingAnalyticsSubmissionTrend",
  REPORT: "/GetTrainingAnalyticsReport",
  SESSION_OPTIONS: "/GetTrainingScheduleBySession",
};

export default function useTeacherTrainingAnalytics() {
  const { user, role } = useContext(AppContext);
  const {
    getDistrictByStateId,
    districts,
    getBlocksByDistrictId,
    blocks,
    getSession,
    sessions,
  } = useMasterData();
  const { fetchTeacherTrainingData } = useTeacherTraining();

  // ---- Filters (same explicit "Search" behaviour as the attendance dashboard) ----
  const [selectedSession, setSelectedSession] = useState<string | undefined>();
  const [trainingTitle, setTrainingTitle] = useState<any>("");
  const [trainingTitleOptions, setTrainingTitleOptions] = useState<any[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<
    string | undefined
  >();
  const [selectedBlock, setSelectedBlock] = useState<string | undefined>();
  const [appliedDistrict, setAppliedDistrict] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<any>({ min: null, max: null });
  const [selectedDate, setSelectedDate] = useState<any>(undefined);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // ---- Data ----
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<any>();
  const [districtPerf, setDistrictPerf] = useState<any[]>([]);
  const [blockPerf, setBlockPerf] = useState<any[]>([]);
  const [trend, setTrend] = useState<any[]>([]);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  // const qs = (districtId?: string, blockId?: string) =>
  //   `districtId=${districtId ?? 0}&blockId=${blockId ?? 0}` +
  //   `&scheduleDate=${moment(selectedDate ?? new Date()).format("YYYY-MM-DD")}` +
  //   `&training_Schedule_Header_Id=${trainingTitle || 0}`;

  // const load = async (url: string, onOk: (d: any) => void, onFail: () => void, msg: string) => {
  //   const res = await fetchTeacherTrainingData(url);
  //   if (res?.isSuccess) onOk(res.data);
  //   else {
  //     onFail();
  //     showToast({ type: "error", message: res?.message || msg });
  //   }
  // };

  // const handleApplyFilter = async () => {
  //   setIsLoading(true);
  //   await Promise.all([
  //     load(`${EP.SUMMARY}?${qs(selectedDistrict, selectedBlock)}`, (d) => setSummary(d?.[0]), () => setSummary(undefined), "Failed to load summary"),
  //     // State-wide district ranking always uses districtId=0
  //     load(`${EP.PERFORMANCE}?${qs("0", "0")}`, (d) => setDistrictPerf(d ?? []), () => setDistrictPerf([]), "Failed to load district performance"),
  //     // Block ranking only when a district is chosen
  //     selectedDistrict
  //       ? load(`${EP.PERFORMANCE}?${qs(selectedDistrict, "0")}`, (d) => setBlockPerf(d ?? []), () => setBlockPerf([]), "Failed to load block performance")
  //       : Promise.resolve(setBlockPerf([])),
  //     load(`${EP.TREND}?${qs(selectedDistrict, selectedBlock)}`, (d) => setTrend(d ?? []), () => setTrend([]), "Failed to load submission tracker"),
  //   ]);
  //   setAppliedDistrict(selectedDistrict);
  //   setLastUpdated(new Date());
  //   setIsLoading(false);
  // };

  const handleApplyFilter = () => {
    getAllData();
    setLastUpdated(new Date());
    setAppliedDistrict(selectedDistrict ?? undefined);
    setLastUpdated(new Date());
  };

  const getAllData = async () => {
    setIsLoading(true);
    const res = await fetchTeacherTrainingData(
      `/GetTrainingSchedulePrePostReportDashboard?districtId=${selectedDistrict ?? 0}&blockId=${selectedBlock ?? 0}&scheduleDate=${selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}&training_Schedule_Header_Id=${trainingTitle ?? 0}`,
    );
    if (res.isSuccess) {
      setSummary(res?.data[0]);
    } else {
      setSummary(undefined);
      showToast({
        type: "error",
        message: res?.message || "Failed to load overall map",
      });
    }
    setIsLoading(false);
  };

  const handleClearFilter = () => {
    if (!role?.includes(ROLES.DISTRICT) && !role?.includes(ROLES.BLOCK))
      setSelectedDistrict(undefined);
    setSelectedBlock(undefined);
    setAppliedDistrict(undefined);
    setTimeout(handleApplyFilter, 0);
  };


   const downloadReport = async () => {
      setIsDownloadingReport(true);
      const res = await fetchTeacherTrainingData(
        `/GetTrainingSchedulePrePostDetailedReport?districtId=${selectedDistrict ?? 0}&blockId=${selectedBlock ?? 0}&scheduleDate=${selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}&training_Schedule_Header_Id=${trainingTitle ?? 0}`,
      );
      if (res.isSuccess) {
        const rows: any[] = res?.data ?? [];
        if (rows.length === 0) {
          showToast({
            type: "error",
            message: "No records to export for this filter",
          });
        } else {
          const fileSuffix = moment(selectedDate ?? new Date()).format(
            "DD-MM-YYYY",
          );
       let PRE_POST_REPORT_COLUMNS: any = [
         { header: "Report Date", key: "reportDate", width: 14 },
         { header: "District", key: "district", width: 16 },
         { header: "Block", key: "block", width: 16 },
         { header: "Training Batch", key: "trainingBatch", width: 18 },
         { header: "Subject", key: "subject", width: 16 },
         { header: "School name", key: "schoolName", width: 24 },
         { header: "School code", key: "schoolCode", width: 14 },
         { header: "Teacher Name", key: "teacherName", width: 20 },
         { header: "Employee Code", key: "teacherEmployeeCode", width: 16 },
         {
           header: "Pre Test Submission Time",
           key: "preTestSubmissionTime",
           width: 20,
         },
         { header: "Pre Test Score", key: "preTestScore", width: 12 },
         { header: "Pre-Test %", key: "preTestPercentage", width: 12 },
         {
           header: "Post Test Submission Time",
           key: "postTestSubmissionTime",
           width: 20,
         },
         { header: "Post-Test Score", key: "postTestScore", width: 12 },
         { header: "Post-Test %", key: "postTestPercentage", width: 12 },
         {
           header: "Learning Gain/Loss",
           key: "learningGainLoss",
           width: 16,
         },
       ];
          generateExcelReport(
            rows,
            PRE_POST_REPORT_COLUMNS,
            `TeacherTraining_PrePost_Report_${fileSuffix}`,
            "Pre Post Report",
          );
        }
      } else {
        showToast({
          type: "error",
          message: res?.message || "Failed to download report",
        });
      }
      setIsDownloadingReport(false);
    };

  // ---- Init (same order as attendance hook) ----
  useEffect(() => {
    getDistrictByStateId(1);
    getSession(false);
    if (role?.includes(ROLES.BLOCK)) {
      setSelectedDistrict(String(user?.details?.blockUserDetails?.district_Id));
      setSelectedBlock(String(user?.details?.blockUserDetails?.block_Id));
    } else if (role?.includes(ROLES.DISTRICT)) {
      setSelectedDistrict(
        String(user?.details?.districtUserDetails?.district_Id),
      );
    }
  }, []);

  useEffect(() => {
    if (selectedDistrict) getBlocksByDistrictId(+selectedDistrict);
  }, [selectedDistrict]);

  useEffect(() => {
    if (sessions?.length > 0) {
      const id = sessions.find(
        (s: any) =>
          s?.session_Name ==
          calculateDefaultSession(parseInt(moment().format("M"))),
      )?.session_Id;
      setSelectedSession(String(id));
    }
  }, [sessions]);

  useEffect(() => {
    if (!selectedSession) return;
    (async () => {
      const res = await fetchTeacherTrainingData(
        `/GetTrainingScheduleBySession?current_Session=${selectedSession}`,
      );
      setTrainingTitleOptions(res?.isSuccess ? (res.data ?? []) : []);
    })();
  }, [selectedSession]);

  useEffect(() => {
    if (trainingTitleOptions.length > 0) {
      setTrainingTitle(
        String(trainingTitleOptions[0]?.training_Schedule_Header_Id),
      );
      setSelectedDate(new Date(trainingTitleOptions[0]?.start_Date));
    }
  }, [trainingTitleOptions]);

  useEffect(() => {
    const opt = trainingTitleOptions.find(
      (o: any) => o?.training_Schedule_Header_Id == trainingTitle,
    );
    if (opt)
      setDateRange({
        min: new Date(opt.start_Date),
        max: new Date(opt.end_Date),
      });
  }, [trainingTitle]);

  useEffect(() => {
    if (trainingTitle && selectedDate && !initialLoadDone) {
      setInitialLoadDone(true);
      handleApplyFilter();
    }
  }, [trainingTitle, selectedDate]);

  // ---- Derived ----
  const toRows = (arr: any[]) =>
    arr
      .map((e) => ({ name: e?.name, value: Math.round(e?.avgPostScore ?? 0) }))
      .sort((a, b) => b.value - a.value);
  const districtRows = useMemo(() => toRows(districtPerf), [districtPerf]);
  const blockRows = useMemo(() => toRows(blockPerf), [blockPerf]);
  const top3 = districtRows.slice(0, 3);
  const bottom3 = districtRows.slice(-3).reverse();
  const trendPoints = useMemo(
    () => trend.map((t) => ({ label: t?.label, value: t?.count ?? 0 })),
    [trend],
  );

  return {
    selectedSession,
    setSelectedSession,
    sessions,
    trainingTitle,
    setTrainingTitle,
    trainingTitleOptions,
    selectedDate,
    setSelectedDate,
    dateRange,
    selectedDistrict,
    setSelectedDistrict,
    selectedBlock,
    setSelectedBlock,
    districts,
    blocks,
    appliedDistrict,
    handleApplyFilter,
    handleClearFilter,
    lastUpdated,
    isLoading,
    summary,
    districtRows,
    blockRows,
    top3,
    bottom3,
    trendPoints,
    downloadReport,
    isDownloadingReport,
  };
}
